import axios from "axios";
import { API_BASE_URL } from "./api";

const SESSION_USER_KEY = "fantasySwimSessionUser";
const ACCESS_TOKEN_KEY = "fantasySwimAccessToken";

const clearSession = () => {
  localStorage.removeItem(SESSION_USER_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
};

const stringifyErrorData = (data) => {
  if (typeof data === "string") {
    return data;
  }

  if (data && typeof data === "object") {
    if (typeof data.detail === "string") {
      return data.detail;
    }
    if (typeof data.message === "string") {
      return data.message;
    }
    try {
      return JSON.stringify(data);
    } catch {
      return "Unserializable error payload.";
    }
  }

  return "";
};

const persistSession = (payload) => {
  const coachName = payload.user.coach_name;
  const displayName = payload.user.display_name || coachName;
  const profileImageDataUrl = payload.user.profile_image_data_url || "";

  localStorage.setItem(ACCESS_TOKEN_KEY, payload.access_token);
  localStorage.setItem(
    SESSION_USER_KEY,
    JSON.stringify({
      username: payload.user.username,
      coachName,
      displayName,
      profileImageDataUrl,
      isAdmin: payload.user.is_admin ?? false,
    }),
  );

  return {
    username: payload.user.username,
    coachName,
    displayName,
    profileImageDataUrl,
    isAdmin: payload.user.is_admin ?? false,
  };
};

export const updateStoredSessionUser = (partialUser) => {
  const existingRaw = localStorage.getItem(SESSION_USER_KEY);
  if (!existingRaw) return null;

  let existing = null;
  try {
    existing = JSON.parse(existingRaw);
  } catch {
    return null;
  }

  const next = {
    ...existing,
    ...partialUser,
  };

  localStorage.setItem(SESSION_USER_KEY, JSON.stringify(next));
  return next;
};

const parseApiError = (error, fallbackMessage) => {
  console.error("Auth request failed", {
    apiBaseUrl: API_BASE_URL,
    code: error?.code,
    message: error?.message,
    status: error?.response?.status,
    data: error?.response?.data,
  });

  if (!error?.response) {
    const code = error?.code || "NO_RESPONSE";
    const message = error?.message || "No response received from server.";
    return `Network error (${code}): ${message}. API base: ${API_BASE_URL}`;
  }

  const status = error?.response?.status;
  const detail = stringifyErrorData(error?.response?.data);

  if ([502, 503, 504].includes(status)) {
    return `Upstream error (${status}): ${detail || "API gateway/backend unavailable."}`;
  }

  if (status === 401) {
    return `Authentication failed (401): ${detail || fallbackMessage}`;
  }

  return `Request failed (${status}): ${detail || fallbackMessage}`;
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const postWithRetry = async (url, payload) => {
  try {
    return await axios.post(url, payload);
  } catch (error) {
    const status = error?.response?.status;
    const shouldRetry = !error?.response || [502, 503, 504].includes(status);
    if (!shouldRetry) {
      throw error;
    }

    await wait(1800);
    return axios.post(url, payload);
  }
};

export const getAccessToken = () => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const registerUser = async ({ username, password, coachName }) => {
  try {
    const response = await postWithRetry(`${API_BASE_URL}/auth/register`, {
      username,
      password,
      coach_name: coachName,
    });

    return {
      ok: true,
      user: persistSession(response.data),
    };
  } catch (error) {
    return {
      ok: false,
      error: parseApiError(error, "Unable to create account."),
    };
  }
};

export const loginUser = async ({ username, password }) => {
  try {
    const response = await postWithRetry(`${API_BASE_URL}/auth/login`, {
      username: username.trim(),
      password,
    });

    return {
      ok: true,
      user: persistSession(response.data),
    };
  } catch (error) {
    return {
      ok: false,
      error: parseApiError(error, "Invalid username or password."),
    };
  }
};

export const recoverPasswordUser = async ({
  username,
  coachName,
  newPassword,
}) => {
  try {
    await axios.post(`${API_BASE_URL}/auth/recover-password`, {
      username,
      coach_name: coachName,
      new_password: newPassword,
    });

    return {
      ok: true,
      message:
        "Password reset successful. You can now log in with your new password.",
    };
  } catch (error) {
    return {
      ok: false,
      error: parseApiError(error, "Unable to reset password."),
    };
  }
};

export const getSessionUser = async () => {
  const token = getAccessToken();
  const storedUser = localStorage.getItem(SESSION_USER_KEY);

  if (!token || !storedUser) {
    return null;
  }

  try {
    const response = await axios.get(`${API_BASE_URL}/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    localStorage.setItem(
      SESSION_USER_KEY,
      JSON.stringify({
        username: response.data.user.username,
        coachName: response.data.user.coach_name,
        displayName: response.data.user.display_name || response.data.user.coach_name,
        profileImageDataUrl: response.data.user.profile_image_data_url || "",
        isAdmin: response.data.user.is_admin ?? false,
      }),
    );

    return {
      username: response.data.user.username,
      coachName: response.data.user.coach_name,
      displayName: response.data.user.display_name || response.data.user.coach_name,
      profileImageDataUrl: response.data.user.profile_image_data_url || "",
      isAdmin: response.data.user.is_admin ?? false,
    };
  } catch (error) {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    clearSession();
    if (
      status === 401 &&
      typeof detail === "string" &&
      detail.includes("expired")
    ) {
      throw new Error("SESSION_EXPIRED");
    }
    return null;
  }
};

export const restoreSessionUser = async () => {
  const token = getAccessToken();
  const storedUser = localStorage.getItem(SESSION_USER_KEY);

  if (!token || !storedUser) {
    return { user: null, error: "" };
  }

  try {
    const sessionUser = await getSessionUser();
    return { user: sessionUser, error: "" };
  } catch (error) {
    clearSession();
    return { user: null, error: "Your session expired. Please log in again." };
  }
};

export const logoutUser = async () => {
  const token = getAccessToken();
  if (token) {
    try {
      await axios.post(
        `${API_BASE_URL}/auth/logout`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    } catch (error) {
      // no-op
    }
  }

  clearSession();
};
